/**
 * AI Insight Modal — réutilisable pour les 3 actions IA Phase 3.
 * Déclenche un POST sur l'URL fournie, affiche le résultat en markdown, gère les crédits.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, Loader2, AlertCircle, Coins, Check } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Markdown ultra-simple → HTML (titres, gras, listes). Suffisant pour notre cas.
 */
const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.*)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2 text-slate-900">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="text-lg font-bold mt-5 mb-2 text-indigo-900">$1</h2>')
    .replace(/^# (.*)$/gm, '<h1 class="text-xl font-bold mt-5 mb-3 text-indigo-900">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900 font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^\s*[-*] (.*)$/gm, '<li class="ml-4 list-disc text-slate-700">$1</li>')
    .replace(/^\s*\d+\.\s(.*)$/gm, '<li class="ml-4 list-decimal text-slate-700">$1</li>')
    .replace(/\n\n/g, '</p><p class="mt-3 text-slate-700 leading-relaxed">');
  return `<p class="text-slate-700 leading-relaxed">${html}</p>`;
};

export default function AIInsightModal({
  open,
  onOpenChange,
  title,
  description,
  endpoint,             // ex: '/career-intel/ai/employee-analysis/EMP_ID'
  costInfo,             // { cost, balance } — affiché en preview
  resultField = 'analysis', // 'analysis' ou 'plan'
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post(endpoint);
      setResult(res.data[resultField] || res.data.analysis || res.data.plan);
      setCredits(res.data.credits || null);
      toast.success('Analyse IA générée');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        setError(`${detail.message} (solde: ${detail.balance}, requis: ${detail.required})`);
      } else {
        setError(typeof detail === 'string' ? detail : 'Erreur lors de la génération IA');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v) => {
    if (!v) { setResult(null); setError(null); setCredits(null); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-insight-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!result && !loading && !error && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-100">
              <div className="flex items-center gap-2 text-sm">
                <Coins className="w-4 h-4 text-indigo-600" />
                <span className="text-slate-700">Coût de cette analyse :</span>
                <Badge className="bg-indigo-600">{costInfo?.cost ?? '—'} crédits</Badge>
              </div>
              <div className="text-xs text-slate-500">
                Solde : <strong className="text-slate-900">{costInfo?.balance ?? '—'}</strong>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              L'analyse est générée par Gemini sur la base des données réelles de votre périmètre RH.
              Les crédits sont déduits uniquement si la génération réussit.
            </p>
          </div>
        )}

        {loading && (
          <div className="py-12 flex flex-col items-center gap-3" data-testid="ai-loading">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            <p className="text-sm text-slate-500">Analyse en cours...</p>
          </div>
        )}

        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3" data-testid="ai-error">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Erreur</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="ai-result">
            {credits && credits.consumed > 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600 bg-emerald-50 border border-emerald-100 p-2 rounded">
                <Check className="w-4 h-4 text-emerald-600" />
                {credits.consumed} crédits utilisés · nouveau solde : <strong>{credits.balance_after}</strong>
              </div>
            )}
            {credits && credits.free && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 p-2 rounded">
                Mode SuperAdmin : aucun crédit déduit.
              </div>
            )}
            <div
              className="prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(result) }}
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          {!result && (
            <Button onClick={run} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-run-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</> : <><Sparkles className="w-4 h-4 mr-2" />Lancer l'analyse</>}
            </Button>
          )}
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="ai-close-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
